/**
 * Route-family detection for the global loading screen: distinguishes the
 * code/math IDE routes (which show the {@link import('./IdeLoading').IDELoading}
 * editor skeleton) from every other route (which shows the standard Karel
 * loader, {@link import('./StandardLoading').StandardLoading}).
 */

/**
 * Subpages that render the IDE editor skeleton while loading — the
 * `/:courseId/{subpage}/...` segments for the code IDE (`ide`) and the math
 * IDE (`mide`). See the `ide/*` and `mide/*` routes in `CourseRoutes.tsx`.
 *
 * Kept as an explicit allowlist so a route whose path merely *contains* the
 * letters "ide" — `slides` (sl-**ide**-s), a lesson's `:slideId`, a deck id,
 * `video`, `guide` — never triggers the editor skeleton.
 */
export const IDE_ROUTE_SUBPAGES: readonly string[] = ['ide', 'mide'];

/**
 * Determines whether `pathname` addresses an IDE route, i.e. one whose subpage
 * (the segment after the course id in `/:courseId/{subpage}/...`) is one of
 * {@link IDE_ROUTE_SUBPAGES}.
 *
 * Matches on the exact path *segment*, not a substring, so `/:courseId/slides`
 * is correctly treated as a non-IDE route despite containing "ide" inside
 * "slides". A whole-string `includes('ide')` check is what previously mis-fired
 * the editor skeleton on `slides` (and every other "…ide…" path).
 *
 * @param pathname - A URL pathname, e.g. `window.location.pathname`. Leading,
 *     trailing, and repeated slashes are tolerated. When it has no subpage
 *     segment (the root, or a single-segment path like `/:courseId`), the
 *     result is `false`.
 * @return `true` if the route is an IDE route, otherwise `false`.
 * @throws Never.
 */
export function isIdeRoute(pathname: string): boolean {
  const segments = pathname.split('/').filter(segment => segment !== '');
  // segments[0] is the course id; segments[1] is the subpage.
  const subpage = segments[1];
  return subpage !== undefined && IDE_ROUTE_SUBPAGES.includes(subpage);
}
